/**
 * Pet Food Affiliate Program
 * Breeders recommend Hill's/Royal Canin, earn referral fees
 */

import { useState, useEffect } from 'react';
import { Package, DollarSign, Link2, Copy, Check, TrendingUp, Users, ShoppingBag, Gift, QrCode, Mail, Download, Star, Info } from 'lucide-react';
import { Card, Button, Badge, Modal, Input, Alert, Tabs } from '../shared/UIComponents';
import { formatCurrency, formatDate, cn } from '../../utils/helpers';
import { useAuth } from '../../hooks/useAuth';
import { collection, addDoc, query, where, getDocs, orderBy, serverTimestamp, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';

const FOOD_PARTNERS = {
  hills: {
    name: "Hill's Science Diet",
    logo: '🏔️',
    color: 'bg-blue-500',
    lightColor: 'bg-blue-100 text-blue-700',
    description: "Veterinarian recommended nutrition",
    breederCommission: 0.05,
    platformCommission: 0.03,
    products: [
      { id: 'hills-kitten', name: "Kitten Chicken", size: '3.5kg', price: 650 },
      { id: 'hills-kitten-lg', name: "Kitten Chicken", size: '7kg', price: 1150 },
      { id: 'hills-adult', name: "Adult Indoor", size: '3.5kg', price: 620 },
      { id: 'hills-sensitive', name: "Sensitive Stomach", size: '3.5kg', price: 720 },
    ]
  },
  royalcanin: {
    name: "Royal Canin",
    logo: '👑',
    color: 'bg-red-500',
    lightColor: 'bg-red-100 text-red-700',
    description: "Breed-specific nutrition",
    breederCommission: 0.04,
    platformCommission: 0.03,
    products: [
      { id: 'rc-kitten', name: "Kitten", size: '4kg', price: 750 },
      { id: 'rc-kitten-lg', name: "Kitten", size: '10kg', price: 1650 },
      { id: 'rc-maine', name: "Maine Coon Adult", size: '4kg', price: 820 },
      { id: 'rc-persian', name: "Persian Adult", size: '4kg', price: 820 },
      { id: 'rc-british', name: "British Shorthair", size: '4kg', price: 820 },
      { id: 'rc-indoor', name: "Indoor Adult", size: '4kg', price: 680 },
    ]
  }
};

const REFERRAL_STATUS = {
  pending: { label: 'Pending', color: 'bg-amber-100 text-amber-700' },
  clicked: { label: 'Clicked', color: 'bg-blue-100 text-blue-700' },
  purchased: { label: 'Purchased', color: 'bg-paw-100 text-paw-700' },
  paid: { label: 'Paid', color: 'bg-green-100 text-green-700' },
};

export const FoodAffiliateProgram = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [referrals, setReferrals] = useState([]);
  const [stats, setStats] = useState({ totalEarnings: 0, pendingEarnings: 0, totalReferrals: 0, conversions: 0 });
  const [activeTab, setActiveTab] = useState('overview');
  const [showCreateLink, setShowCreateLink] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);

  useEffect(() => { if (user?.catteryId) loadData(); }, [user?.catteryId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const q = query(collection(db, 'food_referrals'), where('catteryId', '==', user.catteryId), orderBy('createdAt', 'desc'));
      const snap = await getDocs(q);
      const data = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setReferrals(data);
      
      const totalEarnings = data.filter(r => r.status === 'paid').reduce((sum, r) => sum + (r.breederCommission || 0), 0);
      const pendingEarnings = data.filter(r => r.status === 'purchased').reduce((sum, r) => sum + (r.breederCommission || 0), 0);
      const conversions = data.filter(r => ['purchased', 'paid'].includes(r.status)).length;
      setStats({ totalEarnings, pendingEarnings, totalReferrals: data.length, conversions });
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateCode = () => {
    const prefix = user.catteryId?.substring(0, 4).toUpperCase() || 'CAT';
    return `${prefix}-${Math.random().toString(36).substring(2, 6).toUpperCase()}`;
  };

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'products', label: 'Products' },
    { id: 'referrals', label: 'My Referrals', count: referrals.length },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-display font-bold text-gray-900">Food Partner Program</h1>
          <p className="text-gray-500">Earn commission recommending premium pet food</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowSendModal(true)}><Mail className="w-4 h-4" />Send to Buyer</Button>
          <Button onClick={() => setShowCreateLink(true)}><Link2 className="w-4 h-4" />Create Link</Button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-paw-500 to-paw-600 text-white">
          <p className="text-sm text-white/80">Total Earned</p>
          <p className="text-2xl font-bold">{formatCurrency(stats.totalEarnings)}</p>
        </Card>
        <Card>
          <p className="text-sm text-gray-500">Pending</p>
          <p className="text-2xl font-bold text-amber-600">{formatCurrency(stats.pendingEarnings)}</p>
        </Card>
        <Card>
          <p className="text-sm text-gray-500">Total Referrals</p>
          <p className="text-2xl font-bold">{stats.totalReferrals}</p>
        </Card>
        <Card>
          <p className="text-sm text-gray-500">Conversions</p>
          <p className="text-2xl font-bold">{stats.conversions}</p>
        </Card>
      </div>

      <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />

      {activeTab === 'overview' && <OverviewTab />}
      {activeTab === 'products' && <ProductsTab onCreateLink={() => setShowCreateLink(true)} />}
      {activeTab === 'referrals' && <ReferralsTab referrals={referrals} loading={loading} />}

      <CreateLinkModal isOpen={showCreateLink} onClose={() => setShowCreateLink(false)} catteryId={user?.catteryId} generateCode={generateCode} onSuccess={loadData} />
      <SendModal isOpen={showSendModal} onClose={() => setShowSendModal(false)} catteryId={user?.catteryId} catteryName={user?.catteryName} generateCode={generateCode} onSuccess={loadData} />
    </div>
  );
};

const OverviewTab = () => (
  <div className="space-y-6">
    <Card>
      <h3 className="font-semibold text-gray-900 mb-4">How It Works</h3>
      <div className="grid grid-cols-4 gap-4 text-center">
        <div><div className="w-10 h-10 bg-feline-100 rounded-full flex items-center justify-center mx-auto mb-2 text-feline-600 font-bold">1</div><p className="text-sm">Create Link</p></div>
        <div><div className="w-10 h-10 bg-feline-100 rounded-full flex items-center justify-center mx-auto mb-2 text-feline-600 font-bold">2</div><p className="text-sm">Share with Buyers</p></div>
        <div><div className="w-10 h-10 bg-feline-100 rounded-full flex items-center justify-center mx-auto mb-2 text-feline-600 font-bold">3</div><p className="text-sm">They Purchase</p></div>
        <div><div className="w-10 h-10 bg-paw-100 rounded-full flex items-center justify-center mx-auto mb-2 text-paw-600 font-bold">4</div><p className="text-sm">You Earn!</p></div>
      </div>
    </Card>

    <div className="grid grid-cols-2 gap-6">
      {Object.entries(FOOD_PARTNERS).map(([key, brand]) => (
        <Card key={key}>
          <div className="flex items-center gap-4">
            <div className={cn('w-14 h-14 rounded-xl flex items-center justify-center text-white text-2xl', brand.color)}>{brand.logo}</div>
            <div>
              <h3 className="font-semibold">{brand.name}</h3>
              <p className="text-sm text-gray-500">{brand.description}</p>
              <p className="text-lg font-bold text-paw-600 mt-1">{brand.breederCommission * 100}% commission</p>
            </div>
          </div>
        </Card>
      ))}
    </div>

    <Alert type="info">
      <p className="text-sm"><strong>Commission paid monthly</strong> once you reach R200 minimum. 30-day tracking window on all links.</p>
    </Alert>
  </div>
);

const ProductsTab = ({ onCreateLink }) => {
  const [brand, setBrand] = useState('hills');
  const b = FOOD_PARTNERS[brand];

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        {Object.entries(FOOD_PARTNERS).map(([key, br]) => (
          <button key={key} onClick={() => setBrand(key)} className={cn('px-4 py-2 rounded-xl font-medium', brand === key ? `${br.color} text-white` : 'bg-gray-100')}>{br.logo} {br.name}</button>
        ))}
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        {b.products.map((p) => (
          <Card key={p.id}>
            <div className="flex items-center gap-2 mb-2">
              <span className={cn('w-10 h-10 rounded-lg flex items-center justify-center text-lg', b.lightColor)}>{b.logo}</span>
              <div><p className="font-medium">{p.name}</p><p className="text-xs text-gray-500">{p.size}</p></div>
            </div>
            <div className="flex justify-between items-center">
              <div>
                <p className="font-bold">{formatCurrency(p.price)}</p>
                <p className="text-xs text-paw-600">Earn {formatCurrency(p.price * b.breederCommission)}</p>
              </div>
              <Button size="sm" onClick={onCreateLink}><Link2 className="w-4 h-4" /></Button>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
};

const ReferralsTab = ({ referrals, loading }) => {
  const [copied, setCopied] = useState(null);
  const copy = (link, id) => { navigator.clipboard.writeText(link); setCopied(id); setTimeout(() => setCopied(null), 2000); };

  if (loading) return <Card><div className="animate-pulse space-y-4">{[...Array(3)].map((_, i) => <div key={i} className="h-16 bg-gray-100 rounded" />)}</div></Card>;
  if (!referrals.length) return <Card className="text-center py-12"><Link2 className="w-12 h-12 text-gray-300 mx-auto mb-4" /><p className="text-gray-500">No referrals yet</p></Card>;

  return (
    <Card className="p-0 overflow-hidden">
      <div className="divide-y">
        {referrals.map((r) => {
          const brand = FOOD_PARTNERS[r.brand];
          const status = REFERRAL_STATUS[r.status];
          return (
            <div key={r.id} className="p-4 flex items-center gap-4">
              <div className={cn('w-10 h-10 rounded-lg flex items-center justify-center', brand?.lightColor)}>{brand?.logo}</div>
              <div className="flex-1">
                <p className="font-medium">{r.buyerName || 'Anonymous'}</p>
                <p className="text-xs text-gray-500">{brand?.name} • {r.code}</p>
              </div>
              <Badge className={status?.color}>{status?.label}</Badge>
              {r.breederCommission && <p className="text-paw-600 font-medium">+{formatCurrency(r.breederCommission)}</p>}
              <button onClick={() => copy(r.link, r.id)} className="p-2 hover:bg-gray-100 rounded-lg">
                {copied === r.id ? <Check className="w-4 h-4 text-paw-500" /> : <Copy className="w-4 h-4 text-gray-400" />}
              </button>
            </div>
          );
        })}
      </div>
    </Card>
  );
};

const CreateLinkModal = ({ isOpen, onClose, catteryId, generateCode, onSuccess }) => {
  const [brand, setBrand] = useState('hills');
  const [product, setProduct] = useState('');
  const [buyerName, setBuyerName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [link, setLink] = useState(null);
  const [copied, setCopied] = useState(false);

  const create = async () => {
    setSubmitting(true);
    const code = generateCode();
    const b = FOOD_PARTNERS[brand];
    const newLink = `https://feline-sa.co.za/shop?ref=${code}&brand=${brand}${product ? `&product=${product}` : ''}`;
    
    await addDoc(collection(db, 'food_referrals'), {
      catteryId, code, link: newLink, brand, productId: product || null, buyerName: buyerName || null,
      status: 'pending', breederCommissionRate: b.breederCommission, platformCommissionRate: b.platformCommission,
      createdAt: serverTimestamp(), expiresAt: Timestamp.fromDate(new Date(Date.now() + 30*24*60*60*1000))
    });
    
    setLink(newLink);
    setSubmitting(false);
    onSuccess();
  };

  const copy = () => { navigator.clipboard.writeText(link); setCopied(true); setTimeout(() => setCopied(false), 2000); };
  const b = FOOD_PARTNERS[brand];

  return (
    <Modal isOpen={isOpen} onClose={() => { onClose(); setLink(null); }} title="Create Referral Link">
      {link ? (
        <div className="space-y-4">
          <Alert type="success">Link created!</Alert>
          <div className="flex gap-2">
            <code className="flex-1 text-sm bg-gray-50 p-2 rounded overflow-x-auto">{link}</code>
            <Button size="sm" onClick={copy}>{copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}</Button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Button variant="outline" onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(link)}`, '_blank')}>WhatsApp</Button>
            <Button variant="outline" onClick={() => window.open(`mailto:?subject=Recommended cat food&body=${encodeURIComponent(link)}`, '_blank')}>Email</Button>
          </div>
          <Button variant="ghost" className="w-full" onClick={() => { onClose(); setLink(null); }}>Done</Button>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-2">
            {Object.entries(FOOD_PARTNERS).map(([key, br]) => (
              <button key={key} onClick={() => { setBrand(key); setProduct(''); }} className={cn('p-3 rounded-xl border-2 text-left', brand === key ? 'border-feline-500 bg-feline-50' : 'border-gray-200')}>
                <span className="text-xl">{br.logo}</span>
                <p className="font-medium">{br.name}</p>
                <p className="text-xs text-paw-600">{br.breederCommission * 100}%</p>
              </button>
            ))}
          </div>
          <select value={product} onChange={(e) => setProduct(e.target.value)} className="w-full p-2.5 border rounded-xl">
            <option value="">All {b.name} Products</option>
            {b.products.map(p => <option key={p.id} value={p.id}>{p.name} ({p.size}) - {formatCurrency(p.price)}</option>)}
          </select>
          <Input label="Buyer Name (optional)" value={buyerName} onChange={(e) => setBuyerName(e.target.value)} placeholder="Track who you sent to" />
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            <Button onClick={create} loading={submitting}><Link2 className="w-4 h-4" />Create</Button>
          </div>
        </div>
      )}
    </Modal>
  );
};

const SendModal = ({ isOpen, onClose, catteryId, catteryName, generateCode, onSuccess }) => {
  const [buyerName, setBuyerName] = useState('');
  const [buyerEmail, setBuyerEmail] = useState('');
  const [kittenName, setKittenName] = useState('');
  const [brand, setBrand] = useState('hills');
  const [sending, setSending] = useState(false);

  const send = async () => {
    if (!buyerEmail) return;
    setSending(true);
    const code = generateCode();
    const b = FOOD_PARTNERS[brand];
    const link = `https://feline-sa.co.za/shop?ref=${code}&brand=${brand}`;
    
    await addDoc(collection(db, 'food_referrals'), {
      catteryId, catteryName, code, link, brand, buyerName, buyerEmail, kittenName,
      status: 'pending', breederCommissionRate: b.breederCommission, platformCommissionRate: b.platformCommission,
      sentViaEmail: true, createdAt: serverTimestamp()
    });

    const subject = encodeURIComponent(`Recommended food for ${kittenName || 'your kitten'}`);
    const body = encodeURIComponent(`Hi ${buyerName},\n\nI recommend ${b.name} for ${kittenName || 'your new kitten'}. Here's my recommended link:\n\n${link}\n\nThis is what we use at ${catteryName}.\n\nBest regards`);
    window.open(`mailto:${buyerEmail}?subject=${subject}&body=${body}`, '_blank');
    
    setSending(false);
    onSuccess();
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Send Food Recommendation">
      <div className="space-y-4">
        <Input label="Buyer Name" value={buyerName} onChange={(e) => setBuyerName(e.target.value)} />
        <Input label="Buyer Email *" type="email" value={buyerEmail} onChange={(e) => setBuyerEmail(e.target.value)} />
        <Input label="Kitten Name" value={kittenName} onChange={(e) => setKittenName(e.target.value)} placeholder="Personalizes the email" />
        <div className="grid grid-cols-2 gap-2">
          {Object.entries(FOOD_PARTNERS).map(([key, b]) => (
            <button key={key} onClick={() => setBrand(key)} className={cn('p-3 rounded-xl border-2', brand === key ? 'border-feline-500 bg-feline-50' : 'border-gray-200')}>
              <span className="text-xl">{b.logo}</span> {b.name}
            </button>
          ))}
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={send} loading={sending} disabled={!buyerEmail}><Mail className="w-4 h-4" />Send</Button>
        </div>
      </div>
    </Modal>
  );
};

export default FoodAffiliateProgram;
